/**
 * Which routes the onboarding redirect must leave alone (app#242).
 *
 * ⚠⚠ THE SIGNAL IS RIGHT AND THE ORDER WAS WRONG. `useOnboard` is
 * `networks.length <= 1 && nodes.length === 0`, which is a correct description of an account that
 * has not started — and it is true of an organization that has just PAID, because subscribing does
 * not create a node. So a customer returning from Stripe Checkout was redirected off
 * `/billing?status=success` into the wizard and saw "Welcome to noBGP" one second after being
 * charged, with nothing on screen saying their money had moved.
 *
 * ⚠⚠ AND IT IS NOT ONLY THE CHECKOUT RETURN. Measured 2026-09-03 on an org with no nodes, all four
 * of these landed on `/onboard/welcome`: the Checkout return, the billing-portal return, an in-app
 * navigation to `/billing`, and a full reload of it. So the exemption cannot be keyed on
 * `?status=` — a customer who goes to `/billing` to subscribe in the first place carries no query
 * at all, and subscribing before provisioning is a normal order. It is the one the Upgrade buttons
 * on an empty org invite.
 *
 * ⚠ The sharp edge this closes is not the detour, it is what the detour hides. `Billing.tsx`'s
 * `finalizing` state — the spinner for the window between Stripe returning and the webhook landing
 * — could never render, because a new org is the only case where that window exists. A customer
 * leaving the wizard inside it read `INACTIVE` / "No plan" beside an Upgrade button seconds after
 * paying, which reads as a failed charge and invites a second one.
 *
 * ⚠ Kept to money routes deliberately. Every other route on an unstarted account still goes to the
 * wizard, which is what onboarding is for. `/account` and the `/org/*` routes were considered and
 * left alone: nothing measured says they are reached before setup, and widening a redirect rule
 * past the evidence is how the next surprise gets built.
 *
 * ⚠ These are the same routes `router.tsx` declares — `usage` and `billing` under the app shell.
 * A route renamed there has to be renamed here, which is why they are named once, here, rather
 * than spelled inline in `AppLayout`.
 */
const EXEMPT = ['/billing', '/usage']

/**
 * True when this path must render even though the account has not started.
 *
 * ⚠ Matches the segment, not the prefix: `/billing` and `/billing/anything` are exempt,
 * `/billingsomething` is not. Neither of these routes takes a child today; the boundary is here so
 * that adding one does not silently drop it out of the exemption.
 */
export const skipsOnboarding = (pathname: string): boolean =>
  EXEMPT.some(route => pathname === route || pathname.startsWith(`${route}/`))
